import React, { useMemo, Fragment } from "react";

import { Autocomplete } from "@material-ui/lab";
import { Checkbox, makeStyles, TextField, Typography } from "@material-ui/core";

import { useSelectedProject } from "modules/projects/hooks/useSelectedProject";

import { getUniqueObjectsArray } from "modules/core/utils/helpers";

const useStyles = makeStyles(theme => ({
  paper: {
    boxShadow: theme.shadows[10]
  }
}));

export interface IProjectSelection {
  value: string;
  label: string;
  hasAccess?: boolean;
}

interface IProps {
  value?: IProjectSelection | IProjectSelection[] | null;
  error?: string;
  isError?: boolean;
  multiple?: boolean;
  onChange: (value: IProjectSelection | IProjectSelection[] | null) => void;
  canAccessProject: (projectId: string) => boolean;
}

const GlobalProjectsSelect: React.FC<IProps> = props => {
  const { value, onChange, error, isError, canAccessProject, multiple } = props;

  const { selectedProject, rollups, rollupsType } = useSelectedProject();

  const projects: IProjectSelection[] = useMemo(() => {
    let options: IProjectSelection[] = [];

    if (rollups) {
      options = rollups.projects
        .filter((project, _index) => {
          if (rollupsType !== "project") {
            if (rollupsType === "collection") {
              const collectionId = selectedProject?.id;
              const groups = rollups.groups.filter(
                group => group.collection_id === collectionId
              );
              return groups.some(group => group.id === project.group_id);
            }
            if (rollupsType === "group") {
              const groupId = selectedProject?.id;
              return project.group_id === groupId;
            }
          }

          return true;
        })
        .map(project => ({
          value: project.id,
          label: project.name,
          hasAccess: canAccessProject(project.id)
        }));
    }

    return getUniqueObjectsArray(options, "value");
  }, [canAccessProject, rollups, rollupsType, selectedProject]);

  const classes = useStyles();

  return (
    <Autocomplete
      fullWidth
      multiple={multiple ?? true}
      disableCloseOnSelect={Array.isArray(value)}
      id="tags-outlined"
      limitTags={2}
      options={projects}
      getOptionLabel={(option: IProjectSelection) => option.label}
      value={value}
      onChange={(event, value) => onChange(value)}
      classes={{ paper: classes.paper }}
      getOptionDisabled={option => !option.hasAccess}
      groupBy={option => (option.hasAccess ? "access" : "no-access")}
      renderGroup={({ group, children, key }) => (
        <Fragment key={key}>
          {group === "no-access" && (
            <Typography
              variant="caption"
              style={{ paddingLeft: "20px", fontWeight: 700 }}
            >
              Projects you don&apos;t have access to
            </Typography>
          )}

          {children}
        </Fragment>
      )}
      size="small"
      renderOption={(option, { selected }) => (
        <>
          <Checkbox
            size="small"
            style={{ marginRight: 8 }}
            checked={selected}
          />

          <Typography
            color={option.value === "all" ? "primary" : "textPrimary"}
            variant="body2"
            style={{
              fontWeight: option.value === "all" ? 600 : 500
            }}
          >
            {option.label}
          </Typography>
        </>
      )}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          label="Projects"
          placeholder="Projects"
          error={isError}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            style: { minHeight: 40 },
            autoComplete: "new-password"
          }}
        />
      )}
    />
  );
};

export default GlobalProjectsSelect;
